/**
 * «Культуры хозяйства» — очередь разбора матчинга культур (тенант, Трек 2).
 *
 * Тонкий слой TanStack Query поверх tenant-scoped API из
 * `api/v1/reference/crop_queue.py` (PR #500, ветка ak-crop-matcher-queue-backend).
 * Оперирует per-tenant `local_catalog_items` (entity_type='crop'), НЕ системным
 * `reference.crops`. Типы — строго под DTO бэкенда (CropQueueItem / CropQueueList
 * / RematchOut). Ручной подбор канона использует существующий системный
 * crops-list: GET /api/v1/reference/crops?search=… (envelope { total, items }).
 *
 * Все пути и поля — по контракту #500; менять здесь только вслед за бэком.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { type CropLike } from '@/lib/cropLabel';

const BASE = '/api/v1/reference/tenant-crops';

/** Статусы матча термина культуры (совпадает с Literal бэкенда). */
export type CropMatchStatus = 'matched' | 'suggested' | 'queued' | 'not_a_crop';

/**
 * Что означает термин, помеченный «не культура»:
 *  · `field_state`  — состояние поля (пар, АМП, под мелиорацией);
 *  · `land_parcel`  — тип угодья (залежь, сенокос, пастбище, сад, лес);
 *  · `crop_mix`     — травосмесь / смесь культур.
 */
export type NotACropKind = 'field_state' | 'land_parcel' | 'crop_mix';

/** Причина эскалации термина на разбор платформе. */
export type EscalateReason = 'new_crop' | 'other';

/**
 * Строка состава тенант-культуры-смеси — DTO `CropComponent`.
 * «Многолетние травы (люц+эсп)» = Люцерна 60% + Эспарцет 40%.
 */
export interface CropComponent {
  crop_id: string;
  crop_name_ru: string | null;
  /** Доля в %. Может отсутствовать: «смесь этих культур, пропорции не знаю». */
  share_pct: number | string | null;
}

/** Строка состава на запись — DTO `CropComponentIn`. */
export interface CropComponentInput {
  crop_id: string;
  share_pct: number | null;
}

/** Ответ GET/PUT состава — DTO `ComponentsOut`. */
export interface ComponentsResponse {
  items: CropComponent[];
  /** Мягкое предупреждение (сумма долей ≠ 100) — НЕ ошибка. */
  warning: string | null;
}

/** Строка очереди — DTO `CropQueueItem`. */
/** Одна область видимости записи — строка членства. */
export interface CropScope {
  scope_level: 'holding' | 'cluster' | 'farm';
  scope_ref_id: string | null;
  /** Имя кластера/хозяйства; у холдинга пусто. */
  name?: string | null;
}

/**
 * Где запись видна — одной человеческой строкой.
 *
 * «Весь холдинг» / «Кластер «Юг»» / «Кластер «Юг» и ещё 2». Перечислять
 * пять названий в ячейке списка бессмысленно: колонка отвечает на вопрос
 * «всем или не всем», подробности — в карточке.
 */
export function cropVisibilityLabel(item: CropQueueItem): string {
  return scopesLabel(
    item.scopes?.length
      ? item.scopes
      : [
          {
            scope_level: item.scope_level ?? 'holding',
            scope_ref_id: item.scope_ref_id ?? null,
            name: item.scope_name ?? null,
          },
        ],
  );
}

/** Набор областей одной строкой — общая часть подписи (список, карточка, слияние). */
export function scopesLabel(scopes: CropScope[]): string {
  if (scopes.length === 0) return '—';
  if (scopes.some((s) => s.scope_level === 'holding')) return 'Весь холдинг';
  // «Голова» подписи детерминирована: кластеры вперёд, дальше по имени. Иначе
  // «Кластер «Юг» и ещё 2» после рефетча становится «Заречное и ещё 2» — то же
  // самое положение дел, а строка мигает.
  const ordered = [...scopes].sort((a, b) => {
    if (a.scope_level !== b.scope_level) return a.scope_level === 'cluster' ? -1 : 1;
    return (a.name ?? '').localeCompare(b.name ?? '', 'ru');
  });
  const first = ordered[0];
  const head =
    first.scope_level === 'cluster' ? `Кластер «${first.name ?? '—'}»` : (first.name ?? '—');
  return ordered.length > 1 ? `${head} и ещё ${ordered.length - 1}` : head;
}

export interface CropQueueItem {
  id: string;
  local_name: string | null;
  match_status: CropMatchStatus | string;
  match_confidence: number | null;
  matched_by: string | null;
  ref_id: string | null;
  /** ru-имя сопоставленного / предложенного канона. */
  canonical_name: string | null;
  /**
   * Класс культуры, унаследованный от `reference.crops` (не хранится в LCI).
   * `annual` / `winter` / `perennial`; `null`, если канон ещё не привязан
   * (`ref_id === null`). Правило: perennial доминирует над winter
   * (F3 ADR-060 v3, spec reference-architecture v1 SA §1.1 crops MVP).
   */
  crop_class: 'annual' | 'winter' | 'perennial' | null;
  /** Число посевов, ссылающихся на термин (0 если API не даёт). */
  field_count: number;
  /** Назначение посева (цель выращивания), если задано при сопоставлении. */
  growing_goal_id?: string | null;
  growing_goal_name_ru?: string | null;
  /** Подтип «не культуры» (что это на самом деле). */
  not_a_crop_kind?: NotACropKind | null;
  /**
   * Конкретное значение подтипа. Контракт чтения гарантирует только
   * `not_a_crop_kind`; коды приходят опционально — если бэкенд их не отдаёт,
   * UI показывает только вид («Состояние поля»), без значения.
   */
  field_state_code?: string | null;
  land_category_code?: string | null;
  /** Сколько культур в составе смеси (0 — обычный термин). */
  components_count?: number;
  /** Состав смеси (для чипа «Люцерна 60% · Эспарцет 40%»). */
  components?: CropComponent[];
  /** Человеческие имена подтипа — их показывает вкладка «Справочник». */
  field_state_name?: string | null;
  land_category_name?: string | null;
  /**
   * Первичная область (совместимость). Правда о видимости — `scopes` ниже:
   * с AK-570 областей может быть несколько.
   */
  scope_level?: 'holding' | 'cluster' | 'farm';
  scope_ref_id?: string | null;
  /** Имя кластера/хозяйства для scope_level != 'holding' («Заречное»). */
  scope_name?: string | null;
  /**
   * ВСЕ области, где видна запись (AK-570/AK-551, решение founder 10.08:
   * «или весь холдинг, или кластер(ы) + отдельные хозяйства вне их»).
   * У записи, заведённой до фичи, ровно одна область — та же, что в паре.
   */
  scopes?: CropScope[];
  /**
   * Цвет, которым запись рисуется на карте и в легендах: свой цвет хозяйства,
   * если он задан, иначе системный (AK-548).
   */
  crop_color?: string | null;
  /** Цвет из системного справочника — чтобы показать «Вернуть системный». */
  system_color?: string | null;
  /** Свой цвет хозяйства; `null` — переопределения нет. */
  color_override?: string | null;
  /** Запись в архиве (С-4/С-5); `null` — активная. */
  archived_at?: string | null;
  /** Кто заархивировал — карточка пишет «В архиве с 28.06 · Петров А.». */
  archived_by_user_id?: string | null;
  archived_by_name?: string | null;
  /**
   * Провенанс слияния (AK-569): запись ушла в архив не сама — её схлопнули в
   * другую. Карточка пишет «слита в «Пшеница озимая»», иначе понять, куда
   * уехали посевы, можно только по косвенным признакам (урок AK-562).
   */
  merged_into_item_id?: string | null;
  merged_into_name?: string | null;
  merged_at?: string | null;
}

/** «12 посевов» / «1 посев» / «нет посевов» — вес записи человеческим языком. */
export function sowingsCountLabel(n: number): string {
  if (n <= 0) return 'нет посевов';
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = 'посевов';
  if (mod10 === 1 && mod100 !== 11) word = 'посев';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) word = 'посева';
  return `${n} ${word}`;
}

/** Тело POST /tenant-crops/merge — DTO `CropMergeIn`. */
export interface CropMergePayload {
  target_id: string;
  source_ids: string[];
  dry_run: boolean;
  /** Явное согласие слить записи с РАЗНОЙ привязкой «в системе это». */
  allow_mixed_refs?: boolean;
}

/** Ответ слияния — DTO `CropMergeOut`. Ключи счётчиков = `таблица.колонка`. */
export interface CropMergeResult {
  target_id: string;
  source_ids: string[];
  dry_run: boolean;
  per_source: Array<{
    source_id: string;
    repointed: Record<string, number>;
    deleted_conflicts: Record<string, number>;
    archived: boolean;
    no_op: boolean;
  }>;
  archived: string[];
  mixed_ref_ids: string[];
  /**
   * Где выжившая будет видна ПОСЛЕ слияния — ФАКТ от сервера. Раньше экран
   * считал это сам («если среди выбранных есть холдинг — значит холдинг»), а
   * сервер область не расширял: обещание на необратимом действии было
   * неправдой.
   */
  result_scopes: CropScope[];
  total_repointed: number;
  total_deleted: number;
  message: string;
}

/** Чем запись «занята» — DTO `UsageOut`, цифры гарда ДО действия. */
export interface CropRecordUsage {
  sowings: number;
  /** Плановые посевы — при них архивация запрещена совсем (исход 3). */
  planned_sowings: number;
  /** Растущие — архивация отложенная, они доживают до уборки (исход 2). */
  growing_sowings: number;
}

/** Правка карточки — DTO `RecordPatch`. Поля без значения не отправляем. */
export interface CropRecordPatch {
  local_name?: string;
  /** `null` = вернуть системный цвет (кнопка в карточке). */
  color_override?: string | null;
  scope_level?: 'holding' | 'cluster' | 'farm';
  scope_ref_id?: string | null;
  /**
   * Полный набор областей видимости (AK-570). PUT-семантика: шлём все
   * галочки, а не дельту — экран всегда знает итоговый набор целиком.
   */
  scopes?: CropScope[];
  /** Смена эталона — под гардом «по записи нет посевов». */
  ref_id?: string;
}

/** Счётчики по статусам — DTO `CropQueueSummary`. */
export interface CropQueueSummary {
  total: number;
  matched: number;
  suggested: number;
  queued: number;
  not_a_crop: number;
}

/** Ответ листинга — DTO `CropQueueList`. */
export interface CropQueueList {
  summary: CropQueueSummary;
  items: CropQueueItem[];
}

/** Итог прогона авто-матчера — DTO `RematchOut`. */
export interface RematchOut {
  matched: number;
  suggested: number;
  queued: number;
  processed: number;
}

/** Позиция системного справочника культур (для панели ручного подбора). */
export interface CropSearchItem extends CropLike {
  id: string;
  name_translations?: { ru?: string | null; en?: string | null } | null;
  latin_name?: string | null;
  code?: string | null;
  category?: string | null;
}

interface CropSearchResponse {
  total: number;
  items: CropSearchItem[];
}

export const tenantCropKeys = {
  all: ['tenant-crops'] as const,
  list: (
    status: CropMatchStatus | 'all',
    kind?: NotACropKind | null,
    archived?: boolean,
  ) => ['tenant-crops', 'list', status, kind ?? null, archived ?? false] as const,
  item: (itemId: string) => ['tenant-crops', 'item', itemId] as const,
  usage: (itemId: string) => ['tenant-crops', 'usage', itemId] as const,
  search: (q: string) => ['tenant-crops', 'canon-search', q] as const,
  components: (itemId: string) =>
    ['tenant-crops', 'components', itemId] as const,
};

// ─── read ────────────────────────────────────────────────────────────────

/**
 * Список терминов культур + сводка. `status` фильтрует по match_status;
 * 'all' — без фильтра (суммарная сводка всё равно по всем статусам).
 */
export function useTenantCrops(
  status: CropMatchStatus | 'all',
  kind?: NotACropKind | null,
  /** С-5: архив — фильтр над тем же списком, а не отдельная вкладка. */
  includeArchived = false,
): UseQueryResult<CropQueueList> {
  return useQuery({
    queryKey: tenantCropKeys.list(status, kind, includeArchived),
    queryFn: () => {
      const params = new URLSearchParams();
      if (status !== 'all') params.set('status', status);
      if (kind) params.set('kind', kind);
      if (includeArchived) params.set('include_archived', 'true');
      const qs = params.toString();
      return api.get<CropQueueList>(`${BASE}${qs ? `?${qs}` : ''}`);
    },
  });
}

/**
 * Поиск канона в системном справочнике культур (по названию + народным
 * синонимам — это делает бэкенд). Пустой запрос → пустой список (не бьём API).
 */
export function useCanonSearch(
  query: string,
): UseQueryResult<CropSearchItem[]> {
  const q = query.trim();
  return useQuery({
    queryKey: tenantCropKeys.search(q),
    enabled: q.length >= 2,
    queryFn: async () => {
      const res = await api.get<CropSearchResponse>(
        `/api/v1/reference/crops?search=${encodeURIComponent(q)}&limit=30`,
      );
      return res.items ?? [];
    },
  });
}

// ─── mutations ─────────────────────────────────────────────────────────────

/** Все мутации инвалидируют весь список очереди (сводка тоже пересчитывается). */
function useInvalidateQueue() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: tenantCropKeys.all });
}

/** Прогон авто-матчера по терминам хозяйства («Разобрать по очереди»). */
/**
 * «Добавить культуру» — завести запись справочника из системного каталога.
 *
 * Идемпотентно на бэкенде: культура уже в номенклатуре вернёт существующую
 * строку, дубля не создаст. Инвалидируем весь список — сводка пересчитается.
 */
export function useAdoptCanonCrop() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: (vars: { cropId: string; companyId?: string | null }) =>
      api.post<{ local_catalog_item_id: string }>(`${BASE}/from-canon`, {
        crop_id: vars.cropId,
        company_id: vars.companyId ?? null,
      }),
    onSuccess: invalidate,
  });
}


export function useRematch() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: () => api.post<RematchOut>(`${BASE}/rematch`),
    onSuccess: invalidate,
  });
}

/**
 * suggested → matched (подтвердить предложенный кандидат) + обучение алиаса.
 *
 * `growingGoalId` необязателен: `undefined` — не трогать назначение (тело не
 * шлём), `null` — снять назначение, строка — задать.
 */
export function useConfirmMatch() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: ({
      itemId,
      growingGoalId,
    }: {
      itemId: string;
      growingGoalId?: string | null;
    }) =>
      api.post<CropQueueItem>(
        `${BASE}/${itemId}/confirm`,
        growingGoalId === undefined
          ? undefined
          : { growing_goal_id: growingGoalId },
      ),
    onSuccess: invalidate,
  });
}

/**
 * Перепривязать термин к другому активному канону + обучение алиаса.
 * Опционально фиксирует назначение посева («на зелёный корм», «семенная»),
 * чтобы агроном продолжал выбирать своё привычное название при посеве.
 */
export function useChangeMatch() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: ({
      itemId,
      cropId,
      growingGoalId,
    }: {
      itemId: string;
      cropId: string;
      growingGoalId?: string | null;
    }) =>
      api.post<CropQueueItem>(`${BASE}/${itemId}/change-match`, {
        crop_id: cropId,
        ...(growingGoalId === undefined
          ? {}
          : { growing_goal_id: growingGoalId }),
      }),
    onSuccess: invalidate,
  });
}

/** Тело POST /not-a-crop — подтип обязателен (кроме смеси). */
export type NotACropPayload =
  | { kind: 'field_state'; field_state_code: string }
  | { kind: 'land_parcel'; land_category_code: string }
  | { kind: 'crop_mix' };

/**
 * Пометить «не культура» с подтипом: состояние поля (пар/АМП/мелиорация),
 * тип угодья (залежь/сенокос/пастбище/сад) или смесь культур.
 */
export function useMarkNotACrop() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: ({
      itemId,
      payload,
    }: {
      itemId: string;
      payload: NotACropPayload;
    }) => api.post<CropQueueItem>(`${BASE}/${itemId}/not-a-crop`, payload),
    onSuccess: invalidate,
  });
}

/**
 * Отмена пометки «это не культура» — обратный ход того же действия.
 *
 * Возврат ведёт запись либо назад в культуры (если привязка к канону
 * сохранилась), либо в разбор — тогда она пропадает из списка справочника, и
 * диалог обязан сказать это ДО клика, а не после.
 */
export function useRevertNotACrop() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<CropQueueItem>(`${BASE}/${itemId}/not-a-crop/revert`, {}),
    onSuccess: invalidate,
  });
}

// ─── состав тенант-культуры-смеси ────────────────────────────────────────────

/** Состав смеси одного термина. Без itemId запрос не уходит. */
export function useCropComponents(
  itemId: string | null,
): UseQueryResult<ComponentsResponse> {
  return useQuery({
    queryKey: tenantCropKeys.components(itemId ?? ''),
    enabled: Boolean(itemId),
    queryFn: () => api.get<ComponentsResponse>(`${BASE}/${itemId}/components`),
  });
}

/**
 * Заменить состав смеси целиком (PUT-семантика). Пустой список = очистить.
 * Доли не обязаны давать 100 — бэк вернёт `warning`, но сохранит как есть.
 */
export function useSaveCropComponents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      components,
    }: {
      itemId: string;
      components: CropComponentInput[];
    }) =>
      api.put<ComponentsResponse>(`${BASE}/${itemId}/components`, {
        components,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: tenantCropKeys.components(vars.itemId),
      });
      void qc.invalidateQueries({ queryKey: tenantCropKeys.all });
    },
  });
}

/**
 * Отправить термин на разбор платформе (crop_import_queue).
 * `reason='new_crop'` — нужной культуры нет в справочнике (напр. пшеница
 * твёрдая ≠ мягкая), нужен новый канон.
 */
export function useEscalate() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: ({
      itemId,
      reason,
      note,
    }: {
      itemId: string;
      reason?: EscalateReason;
      note?: string;
    }) => {
      const body: { reason?: EscalateReason; note?: string } = {};
      if (reason) body.reason = reason;
      const trimmed = note?.trim();
      if (trimmed) body.note = trimmed;
      return api.post<CropQueueItem>(
        `${BASE}/${itemId}/escalate`,
        Object.keys(body).length > 0 ? body : undefined,
      );
    },
    onSuccess: invalidate,
  });
}

// ─── карточка записи справочника (состояния 3-4-8 определяющего макета) ──────

/** Сколько посевов стоит за записью — гард показывает это ДО действия. */
export function useCropRecordUsage(
  itemId: string | null,
): UseQueryResult<CropRecordUsage> {
  return useQuery({
    queryKey: tenantCropKeys.usage(itemId ?? ''),
    enabled: itemId !== null,
    queryFn: () => api.get<CropRecordUsage>(`${BASE}/${itemId}/usage`),
  });
}

/** Правка карточки: название, свой цвет, где видна, эталон (под гардом). */
export function usePatchCropRecord() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: (vars: { itemId: string; patch: CropRecordPatch }) =>
      api.patch<CropQueueItem>(`${BASE}/${vars.itemId}`, vars.patch),
    onSuccess: invalidate,
  });
}

/** В архив (С-4) — запись уходит из выбора при новом посеве, история цела. */
export function useArchiveCropRecord() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<CropQueueItem>(`${BASE}/${itemId}/archive`),
    onSuccess: invalidate,
  });
}

/** Вернуть из архива (С-5) — с прежними названием, цветом и видимостью. */
export function useRestoreCropRecord() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<CropQueueItem>(`${BASE}/${itemId}/restore`),
    onSuccess: invalidate,
  });
}

/**
 * С-6: слить дубли записей справочника в одну.
 *
 * ⚠️ Успешный `dry_run` список НЕ инвалидирует (та же грабля, что в AK-564 у
 * тех.операций): счётчики считаются сами при выборе выжившей записи, а
 * инвалидация меняет ссылку на массив выбранных строк — предпросмотр запускался
 * бы заново, и получилась бы бесконечная петля запросов.
 */
export function useMergeCropRecords() {
  const invalidate = useInvalidateQueue();
  return useMutation<CropMergeResult, Error, CropMergePayload>({
    mutationFn: (body) => api.post<CropMergeResult>(`${BASE}/merge`, body),
    onSuccess: (_result, vars) => {
      if (!vars.dry_run) invalidate();
    },
  });
}

/**
 * «В общем справочнике такого нет» — попросить платформу добавить культуру
 * (макет, состояние 5, ветка Б). Записи справочника не создаёт: это заявка.
 */
export function useRequestCanonCrop() {
  return useMutation({
    mutationFn: (vars: { name: string; note?: string }) =>
      api.post<{ status: string; name: string }>(`${BASE}/request-canon`, {
        name: vars.name,
        note: vars.note ?? null,
      }),
  });
}
